import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSession } from "@/lib/session";
import { toast } from "sonner";
import { api, getAuthToken } from "@/lib/api";
import { ShieldCheck, LogOut, Clock } from "lucide-react";
import { useT } from "@/lib/i18n";
import LanguageSelector from "@/components/LanguageSelector";

const TAB_DEFS = [
  { to: "/login",      tKey: "nav.login",     id: "tab-login" },
  { to: "/invoices",   tKey: "nav.invoices",  id: "tab-invoices" },
  { to: "/cashflow",   tKey: "nav.cashflow",  id: "tab-cashflow" },
  { to: "/end-shift",  tKey: "nav.endShift",  id: "tab-end-shift" },
  { to: "/live-view",  tKey: "nav.liveView",  id: "tab-live-view" },
  // Iter 34 — Kronos lives as its own top-nav tab so operators reach the
  // Z-Audit dashboard + history in one tap (was previously a button buried
  // inside Invoices). Permanently visible to every authenticated user.
  { to: "/kronos",     tKey: "nav.kronos",    id: "tab-kronos" },
  // Iter 46 — Intra-Kiosk Chat. Permanently visible to every signed-in
  // operator so emergency SOS broadcasts are always one tap away.
  { to: "/chat",       tKey: "nav.chat",      id: "tab-chat" },
  { to: "/payroll",    tKey: "nav.payroll",   id: "tab-payroll" },
  { to: "/cheques",    tKey: "nav.cheques",   id: "tab-cheques" },
  { to: "/settings",   tKey: "nav.settings",  id: "tab-settings" },
];

// Tabs that get gated when an OCR draft is still on screen. Other tabs
// (NEW LOGIN, INVOICES, LIVE VIEW) remain freely navigable.
const DRAFT_GATED = new Set(["/cashflow", "/end-shift"]);

export default function TopNav() {
  const {
    shift, portalUnlocked, lockPortal, liveViewUnlocked, lockLiveView, setShift,
    pendingDraft,
  } = useSession();
  const nav = useNavigate();
  const t = useT();

  const ownerActive = portalUnlocked || liveViewUnlocked;
  const lockOwner = () => { lockPortal(); lockLiveView(); };

  // SaaS trial countdown — only visible for non-MASTER tenants that hold a JWT
  const [billing, setBilling] = useState(null);
  const [whitelabel, setWhitelabel] = useState(null);
  useEffect(() => {
    if (!getAuthToken()) return;
    api.get("/billing/status")
      .then(({ data }) => setBilling(data))
      .catch(() => setBilling(null));
    api.get("/tenant-settings")
      .then(({ data }) => {
        setWhitelabel(data);
        // Expose the brand color globally so primary buttons (Whitelabel
        // Save, future CTAs) can use `var(--brand-color)` if they opt in.
        document.documentElement.style.setProperty("--brand-color", data.theme_color || "#0F172A");
        document.documentElement.style.setProperty("--brand-text", data.theme_text_color || "#FFFFFF");
      })
      .catch(() => setWhitelabel(null));
  }, []);

  // Theme overrides — fall back to the default white nav when no whitelabel.
  const wl = whitelabel || {};
  const themeColor = wl.theme_color || null;        // e.g. "#0EA5E9"
  const themeText = wl.theme_text_color || "#FFFFFF";
  const platformTitle = wl.platform_title || "KioskForce ERP";
  const subtitle = billing?.tenant_id && billing.tenant_id !== "MASTER_JOHN"
    ? (wl.company_name || t("brand.tagline.tenant"))
    : t("brand.tagline.master");
  const logoUrl = wl.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${wl.logo_url}&v=${wl.logo_file_id || ""}`
    : null;

  const onTabClick = (e, to) => {
    if (DRAFT_GATED.has(to) && pendingDraft) {
      e.preventDefault();
      toast.error(
        "Pending draft on Invoices — tap + ADD TO SHIFT or Discard before continuing",
        { id: "draft-block", duration: 4000 }
      );
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 h-20 border-b shadow-sm flex items-center px-4 sm:px-6 transition-colors"
      style={
        themeColor && themeColor !== "#0F172A"
          ? { backgroundColor: themeColor, color: themeText, borderColor: "rgba(0,0,0,0.15)" }
          : { backgroundColor: "#FFFFFF", color: "#0F172A", borderColor: "#E2E8F0" }
      }
      data-testid="top-nav"
    >
      <button
        onClick={() => nav("/portal")}
        className="mr-4 sm:mr-6 flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-black/10 transition-colors"
        data-testid="brand-link"
        title="Tap brand to enter Private Portal"
      >
        <div
          className="h-10 w-10 rounded-md flex items-center justify-center font-mono font-bold text-lg overflow-hidden bg-white"
          style={!logoUrl ? { backgroundColor: themeColor || "#0F172A", color: themeText } : undefined}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-10 w-10 object-contain" data-testid="brand-logo-img" />
          ) : (
            (platformTitle || "K").charAt(0).toUpperCase()
          )}
        </div>
        <div className="text-left leading-tight">
          <div className="font-bold tracking-tight text-base" data-testid="brand-title">{platformTitle}</div>
          <div className="text-[10px] uppercase tracking-wider opacity-70">{subtitle}</div>
        </div>
      </button>

      <nav className="flex-1 flex items-center gap-1 overflow-x-auto" data-testid="nav-tabs">
        {TAB_DEFS.map((tab) => {
          const gated = DRAFT_GATED.has(tab.to) && pendingDraft;
          // When a custom theme is on, contrast inverts — keep tabs readable.
          const themed = !!themeColor && themeColor !== "#0F172A";
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={tab.id}
              onClick={(e) => onTabClick(e, tab.to)}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center min-h-[64px] px-4 sm:px-5 rounded-lg transition-colors whitespace-nowrap ${
                  isActive
                    ? themed
                      ? "bg-white/95 text-slate-900 shadow-md"
                      : "bg-slate-900 text-white shadow-md"
                    : gated
                    ? "bg-amber-50 text-amber-900 cursor-not-allowed"
                    : themed
                    ? "bg-transparent hover:bg-white/15"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <span className="font-semibold text-sm sm:text-base tracking-wide">{t(tab.tKey)}</span>
              {gated && (
                <span
                  className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-white animate-pulse"
                  data-testid={`${tab.id}-draft-badge`}
                  title="Pending draft on Invoices"
                />
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="flex items-center gap-3 ml-2">
        <LanguageSelector variant={themeColor && themeColor !== "#0F172A" ? "compact" : "light"} />
        {shift && (
          <div className="hidden md:flex flex-col text-right leading-tight pr-2 border-r border-slate-200" data-testid="active-shift-chip">
            <span className="text-xs uppercase tracking-wider text-slate-500">{t("nav.active")}</span>
            <span className="font-mono font-semibold text-sm text-slate-900">
              {shift.employee_name} · {shift.location} · R{shift.register}
            </span>
          </div>
        )}
        {billing && billing.tenant_id !== "MASTER_JOHN" && (
          <button
            onClick={() => nav("/billing")}
            className={`flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md ${
              billing.subscription_status === "active"
                ? "bg-emerald-100 text-emerald-800"
                : billing.trial_days_left > 3
                ? "bg-amber-100 text-amber-800"
                : "bg-red-100 text-red-800"
            }`}
            data-testid="trial-badge"
            title="Open subscription"
          >
            <Clock className="h-3.5 w-3.5" />
            {billing.subscription_status === "active"
              ? "ACTIVE"
              : billing.trial_days_left > 0
              ? `TRIAL · ${billing.trial_days_left}d`
              : "TRIAL EXPIRED"}
          </button>
        )}
        {ownerActive && (
          <button
            onClick={lockOwner}
            className="flex items-center gap-1 text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-md"
            data-testid="portal-lock-btn"
            title="Lock owner sessions"
          >
            <ShieldCheck className="h-4 w-4" />
            Owner
          </button>
        )}
        {shift && (
          <button
            onClick={() => {
              setShift(null);
              nav("/login");
            }}
            data-testid="quick-logout-btn"
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        )}
      </div>
    </header>
  );
}
